import { Application, Assets, Container, Sprite, type Texture, type Ticker } from "pixi.js";
import { gsap } from "gsap";
import type { CameraView, Point, SceneDocument, SceneOverrides } from "./types";
import { artboardToViewport, computeTransform, type ViewportTransform } from "./transforms";

interface RendererOptions {
  document: SceneDocument;
  host: HTMLElement;
  quality: "low" | "high";
  reducedMotion: boolean;
  onHotspots: (positions: ReadonlyMap<string, Point>) => void;
  onFailure: (message: string) => void;
}

interface LayerRuntime {
  spec: SceneDocument["layers"][number];
  sprite: Sprite;
}

const revealOpacity = (progress: number, revealAt: number): number => {
  if (revealAt === 0) return 1;
  return Math.max(0, Math.min(1, (progress - revealAt) / 0.14));
};

export class SceneRenderer {
  readonly app = new Application();
  readonly world = new Container();
  private readonly document: SceneDocument;
  private readonly host: HTMLElement;
  private readonly quality: "low" | "high";
  private readonly onHotspots: RendererOptions["onHotspots"];
  private readonly onFailure: RendererOptions["onFailure"];
  private readonly layers = new Map<string, LayerRuntime>();
  private readonly loadedSources: string[] = [];
  private readonly pointer = { currentX: 0, currentY: 0, targetX: 0, targetY: 0 };
  private camera = { x: 1200, y: 800, zoom: 1 };
  private transform: ViewportTransform = { scale: 1, offset: { x: 0, y: 0 } };
  private overrides: SceneOverrides;
  private emphasis: Record<string, number> = {};
  private assembly = 1;
  private soloId: string | null = null;
  private inspectorActive = false;
  private paused = false;
  private reducedMotion: boolean;
  private destroyed = false;
  private initialized = false;
  private activeFrames = 0;
  private cameraTween: gsap.core.Tween | null = null;
  private resizeObserver: ResizeObserver | null = null;
  private contextLostHandler: ((event: Event) => void) | null = null;
  private fit: "contain" | "cover" = "cover";

  constructor(options: RendererOptions, overrides: SceneOverrides) {
    this.document = options.document;
    this.host = options.host;
    this.quality = options.quality;
    this.reducedMotion = options.reducedMotion;
    this.onHotspots = options.onHotspots;
    this.onFailure = options.onFailure;
    this.overrides = overrides;
    const initial = this.cameraForViewport(this.document.initialCamera);
    this.camera = { x: initial.center.x, y: initial.center.y, zoom: initial.zoom };
  }

  async initialize(): Promise<void> {
    await this.app.init({
      preference: "webgl",
      backgroundAlpha: 0,
      antialias: true,
      autoDensity: true,
      resolution: Math.min(window.devicePixelRatio || 1, 1.5),
      powerPreference: "high-performance",
    });
    this.initialized = true;
    if (this.destroyed) {
      this.app.destroy(true);
      return;
    }

    this.app.stage.addChild(this.world);
    this.world.sortableChildren = true;
    this.host.append(this.app.canvas);
    this.app.canvas.setAttribute("aria-hidden", "true");

    const results = await Promise.allSettled(
      this.document.layers.map(async (spec) => {
        const source = this.quality === "low" && spec.srcLow ? spec.srcLow : spec.src;
        const texture = await Assets.load<Texture>(source);
        return { spec, source, texture };
      }),
    );
    if (this.destroyed) return;

    const failures = results
      .map((result, index) => ({ result, spec: this.document.layers[index] }))
      .filter(({ result, spec }) => result.status === "rejected" && spec?.required);
    if (failures.length > 0) {
      throw new Error(`Required painting layer failed: ${failures.map(({ spec }) => spec?.title).join(", ")}`);
    }

    for (const result of results) {
      if (result.status !== "fulfilled") continue;
      const { spec, source, texture } = result.value;
      this.loadedSources.push(source);
      const sprite = new Sprite(texture);
      sprite.position.set(spec.bounds.x, spec.bounds.y);
      sprite.width = spec.bounds.width;
      sprite.height = spec.bounds.height;
      sprite.zIndex = spec.z;
      sprite.blendMode = spec.blendMode;
      this.world.addChild(sprite);
      this.layers.set(spec.id, { spec, sprite });
    }

    this.resizeObserver = new ResizeObserver(() => this.resize());
    this.resizeObserver.observe(this.host);
    this.host.addEventListener("pointermove", this.handlePointer, { passive: true });
    this.host.addEventListener("pointerleave", this.resetPointer, { passive: true });
    this.contextLostHandler = (event: Event) => {
      event.preventDefault();
      this.pause(true);
      this.onFailure("The graphics context was lost. The complete static painting remains available.");
    };
    this.app.canvas.addEventListener("webglcontextlost", this.contextLostHandler);
    this.app.ticker.add(this.onTick);
    this.resize();
    this.applyLayerState();
    this.wake(2);
  }

  private cameraForViewport(views: { desktop: CameraView; mobile: CameraView }): CameraView {
    return window.matchMedia("(max-width: 800px)").matches ? views.mobile : views.desktop;
  }

  private readonly handlePointer = (event: PointerEvent): void => {
    if (this.paused || this.reducedMotion || event.pointerType === "touch") return;
    const rect = this.host.getBoundingClientRect();
    this.pointer.targetX = Math.max(-1, Math.min(1, ((event.clientX - rect.left) / rect.width) * 2 - 1));
    this.pointer.targetY = Math.max(-1, Math.min(1, ((event.clientY - rect.top) / rect.height) * 2 - 1));
    this.wake(90);
  };

  private readonly resetPointer = (): void => {
    this.pointer.targetX = 0;
    this.pointer.targetY = 0;
    this.wake(60);
  };

  private readonly onTick = (ticker: Ticker): void => {
    if (this.paused || this.reducedMotion) return;
    const ease = Math.min(1, ticker.deltaTime * 0.08);
    this.pointer.currentX += (this.pointer.targetX - this.pointer.currentX) * ease;
    this.pointer.currentY += (this.pointer.targetY - this.pointer.currentY) * ease;
    this.updateLayerPositions();
    this.projectHotspots();
    this.activeFrames -= 1;
    const settled =
      Math.abs(this.pointer.targetX - this.pointer.currentX) < 0.001 &&
      Math.abs(this.pointer.targetY - this.pointer.currentY) < 0.001;
    if (this.activeFrames <= 0 && settled && !this.cameraTween) this.app.ticker.stop();
  };

  private wake(frames = 30): void {
    if (this.destroyed || this.paused || this.reducedMotion) return;
    this.activeFrames = Math.max(this.activeFrames, frames);
    this.app.ticker.start();
  }

  private groupDepth(group: string, canonical: number): number {
    return this.inspectorActive ? (this.overrides.groupDepths[group] ?? canonical) : canonical;
  }

  private groupOffset(group: string, canonicalDepth: number): Point {
    if (this.paused || this.reducedMotion) return { x: 0, y: 0 };
    const depth = this.groupDepth(group, canonicalDepth);
    const artboardPixels = 13 / Math.max(this.transform.scale, 0.001);
    return {
      x: this.pointer.currentX * depth * artboardPixels,
      y: this.pointer.currentY * depth * artboardPixels * 0.65,
    };
  }

  private updateLayerPositions(): void {
    for (const { spec, sprite } of this.layers.values()) {
      const offset = this.groupOffset(spec.motionGroup, spec.depth);
      sprite.position.set(spec.bounds.x + offset.x, spec.bounds.y + offset.y);
    }
  }

  private projectHotspots(): void {
    const points = new Map<string, Point>();
    for (const hotspot of this.document.hotspots) {
      const runtime = this.layers.get(hotspot.layerId);
      if (!runtime) continue;
      const offset = this.groupOffset(runtime.spec.motionGroup, runtime.spec.depth);
      points.set(hotspot.id, artboardToViewport(hotspot.point, this.transform, offset));
    }
    this.onHotspots(points);
  }

  resize(): void {
    if (this.destroyed) return;
    const width = Math.max(1, this.host.clientWidth);
    const height = Math.max(1, this.host.clientHeight);
    this.app.renderer.resize(width, height);
    this.transform = computeTransform(
      { width, height },
      this.document.artboard,
      { center: { x: this.camera.x, y: this.camera.y }, zoom: this.camera.zoom },
      this.fit,
    );
    this.world.scale.set(this.transform.scale);
    this.world.position.set(this.transform.offset.x, this.transform.offset.y);
    this.updateLayerPositions();
    this.projectHotspots();
    this.app.render();
  }

  setCamera(views: { desktop: CameraView; mobile: CameraView }): void {
    const target = this.cameraForViewport(views);
    this.cameraTween?.kill();
    this.cameraTween = null;
    if (this.reducedMotion || this.paused) {
      Object.assign(this.camera, { x: target.center.x, y: target.center.y, zoom: target.zoom });
      this.resize();
      return;
    }
    this.cameraTween = gsap.to(this.camera, {
      x: target.center.x,
      y: target.center.y,
      zoom: target.zoom,
      duration: 0.85,
      ease: "power2.inOut",
      overwrite: true,
      onUpdate: () => this.resize(),
      onComplete: () => {
        this.cameraTween = null;
        this.wake(2);
      },
    });
    this.wake(80);
  }

  setFit(fit: "contain" | "cover"): void {
    if (this.fit === fit) return;
    this.fit = fit;
    this.resize();
  }

  setEmphasis(emphasis: Record<string, number> = {}): void {
    this.emphasis = emphasis;
    this.applyLayerState();
  }

  setInspectorState(overrides: SceneOverrides, assembly: number, soloId: string | null): void {
    this.overrides = overrides;
    this.assembly = assembly;
    this.soloId = soloId;
    this.applyLayerState();
    this.wake(2);
  }

  setInspectorActive(active: boolean): void {
    this.inspectorActive = active;
    this.applyLayerState();
    this.wake(2);
  }

  private applyLayerState(): void {
    for (const [id, { spec, sprite }] of this.layers) {
      const editable = this.inspectorActive ? this.overrides.layers[id] : undefined;
      const soloVisible = !this.inspectorActive || !this.soloId || this.soloId === id;
      const visible = (editable?.visible ?? spec.visible) && soloVisible;
      const opacity = editable?.opacity ?? spec.opacity;
      const assembled = this.inspectorActive ? revealOpacity(this.assembly, spec.revealAt) : 1;
      const emphasized = this.inspectorActive ? 1 : (this.emphasis[id] ?? 1);
      sprite.visible = visible && assembled > 0;
      sprite.alpha = opacity * assembled * emphasized;
    }
    if (!this.destroyed) this.app.render();
  }

  pause(paused: boolean): void {
    this.paused = paused;
    this.cameraTween?.kill();
    this.cameraTween = null;
    this.pointer.targetX = 0;
    this.pointer.targetY = 0;
    this.pointer.currentX = 0;
    this.pointer.currentY = 0;
    this.updateLayerPositions();
    this.projectHotspots();
    if (paused) this.app.ticker.stop();
    else this.wake(2);
    this.app.render();
  }

  setReducedMotion(reduced: boolean): void {
    this.reducedMotion = reduced;
    this.pause(this.paused);
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    this.cameraTween?.kill();
    this.resizeObserver?.disconnect();
    this.host.removeEventListener("pointermove", this.handlePointer);
    this.host.removeEventListener("pointerleave", this.resetPointer);
    if (!this.initialized) return;
    if (this.contextLostHandler) this.app.canvas.removeEventListener("webglcontextlost", this.contextLostHandler);
    this.app.ticker.remove(this.onTick);
    this.app.destroy(true, { children: true });
    void Promise.allSettled(this.loadedSources.map((source) => Assets.unload(source)));
  }
}
